import React from "react";
import { Button } from "reactstrap";

const DEFAULT_HINT =
  "Bảng có nhiều cột, bạn có thể kéo ngang vùng bảng hoặc dùng nút trượt để xem các cột phía sau";

class HorizontalScrollAffordance extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      scrollContentWidth: 0,
      scrollClientWidth: 0,
    };
    this.scrollRef = React.createRef();
    this.topScrollRef = React.createRef();
    this.dragScrollState = null;
  }

  componentDidMount() {
    window.addEventListener("resize", this.syncScrollMetrics);
    window.setTimeout(this.syncScrollMetrics, 0);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.children !== this.props.children ||
      prevProps.minWidth !== this.props.minWidth
    ) {
      window.setTimeout(this.syncScrollMetrics, 0);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.syncScrollMetrics);
    document.removeEventListener("mousemove", this.handleDragScrollMove);
    document.removeEventListener("mouseup", this.handleDragScrollEnd);
  }

  syncScrollMetrics = () => {
    const container = this.scrollRef.current;
    if (!container) return;

    const scrollContentWidth = container.scrollWidth || 0;
    const scrollClientWidth = container.clientWidth || 0;

    if (
      scrollContentWidth !== this.state.scrollContentWidth ||
      scrollClientWidth !== this.state.scrollClientWidth
    ) {
      this.setState({ scrollContentWidth, scrollClientWidth });
    }

    if (this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
  };

  handleScroll = () => {
    const container = this.scrollRef.current;
    if (container && this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
  };

  handleTopScroll = (event) => {
    const container = this.scrollRef.current;
    if (container) {
      container.scrollLeft = event.target.scrollLeft;
    }
  };

  handleScrollBy = (distance) => {
    const container = this.scrollRef.current;
    if (!container) return;

    container.scrollTo({
      left: container.scrollLeft + distance,
      behavior: "smooth",
    });

    window.setTimeout(this.syncScrollMetrics, 250);
  };

  isInteractiveTarget = (target) =>
    Boolean(
      target.closest(
        "input, textarea, select, button, a, .react-select, .custom-control, .dropdown, [role='button']",
      ),
    );

  handleDragScrollStart = (event) => {
    const container = this.scrollRef.current;
    if (
      !container ||
      container.scrollWidth <= container.clientWidth ||
      this.isInteractiveTarget(event.target)
    ) {
      return;
    }

    this.dragScrollState = {
      startX: event.clientX,
      scrollLeft: container.scrollLeft,
    };

    container.classList.add("is-dragging-horizontal");
    document.addEventListener("mousemove", this.handleDragScrollMove);
    document.addEventListener("mouseup", this.handleDragScrollEnd);
  };

  handleDragScrollMove = (event) => {
    if (!this.dragScrollState) return;
    const container = this.scrollRef.current;
    if (!container) return;

    const distance = event.clientX - this.dragScrollState.startX;
    container.scrollLeft = this.dragScrollState.scrollLeft - distance;
    if (this.topScrollRef.current) {
      this.topScrollRef.current.scrollLeft = container.scrollLeft;
    }
    event.preventDefault();
  };

  handleDragScrollEnd = () => {
    const container = this.scrollRef.current;
    if (container) {
      container.classList.remove("is-dragging-horizontal");
    }
    this.dragScrollState = null;
    document.removeEventListener("mousemove", this.handleDragScrollMove);
    document.removeEventListener("mouseup", this.handleDragScrollEnd);
  };

  render() {
    const {
      children,
      hint = DEFAULT_HINT,
      minWidth = 1600,
      className = "",
      toolbarClassName = "",
      scrollStep = 360,
    } = this.props;
    const topScrollWidth =
      this.state.scrollContentWidth || this.state.scrollClientWidth || minWidth;

    return (
      <div className={`horizontal-scroll-affordance ${className}`}>
        <style>{`
          .horizontal-scroll-affordance-toolbar {
            border: 1px solid #e6ebf2;
            border-radius: 12px;
            background: linear-gradient(135deg, #fbfcfe 0%, #f4f8fb 100%);
            padding: 8px 10px;
          }
          .horizontal-scroll-affordance-hint {
            display: inline-flex;
            align-items: center;
            color: #51606f;
            font-size: 0.82rem;
            white-space: nowrap;
          }
          .horizontal-scroll-affordance-top-scroll {
            height: 16px;
            overflow-x: auto;
            overflow-y: hidden;
            flex: 1 1 220px;
            max-width: 520px;
          }
          .horizontal-scroll-affordance-top-scroll::-webkit-scrollbar,
          .horizontal-scroll-affordance-shell::-webkit-scrollbar {
            height: 9px;
          }
          .horizontal-scroll-affordance-top-scroll::-webkit-scrollbar-thumb,
          .horizontal-scroll-affordance-shell::-webkit-scrollbar-thumb {
            background: #b9c7d6;
            border-radius: 999px;
          }
          .horizontal-scroll-affordance-shell {
            overflow-x: auto;
            overflow-y: visible;
            padding-bottom: 8px;
            cursor: grab;
            position: relative;
          }
          .horizontal-scroll-affordance-shell::after {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            width: 36px;
            height: 100%;
            pointer-events: none;
            background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,0.94));
          }
          .horizontal-scroll-affordance-shell.is-dragging-horizontal {
            cursor: grabbing;
            user-select: none;
          }
          .horizontal-scroll-affordance-inner {
            min-width: ${typeof minWidth === "number" ? `${minWidth}px` : minWidth};
          }
        `}</style>
        <div
          className={`horizontal-scroll-affordance-toolbar d-flex flex-wrap align-items-center justify-content-between mb-2 ${toolbarClassName}`}
        >
          <div className="horizontal-scroll-affordance-hint mr-3 mb-1">
            <i className="simple-icon-cursor-move mr-2" />
            {hint}
          </div>
          <div className="d-flex align-items-center flex-grow-1 justify-content-end mb-1">
            <Button
              color="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={() => this.handleScrollBy(-scrollStep)}
            >
              <i className="simple-icon-arrow-left mr-1" />
              Trái
            </Button>
            <div
              ref={this.topScrollRef}
              className="horizontal-scroll-affordance-top-scroll mr-2"
              onScroll={this.handleTopScroll}
            >
              <div style={{ width: topScrollWidth, height: 1 }} />
            </div>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => this.handleScrollBy(scrollStep)}
            >
              Phải
              <i className="simple-icon-arrow-right ml-1" />
            </Button>
          </div>
        </div>
        <div
          ref={this.scrollRef}
          className="horizontal-scroll-affordance-shell"
          onScroll={this.handleScroll}
          onMouseDown={this.handleDragScrollStart}
        >
          <div className="horizontal-scroll-affordance-inner">{children}</div>
        </div>
      </div>
    );
  }
}

export default HorizontalScrollAffordance;
